import { useAuth } from '../../contexts/AuthContext';
import { PRODUCT_LABELS } from './BackshopAlleProdukte';
import { BackshopDuplikatDialog } from '../../components/BackshopDuplikatDialog';
import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  FolderPlus, 
  Package, 
  ShoppingBag, 
  Loader2,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Hash,
  Eye,
  EyeOff,
  List,
  MoreVertical,
  FileText,
  Upload,
  X as XIcon,
  BookOpen,
  Image as ImageIcon,
  Sparkles,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


// Sortable Product Item Component - Memoized for performance
const SortableProductItem = memo(({ product, onEdit, onDelete, onToggleActive }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });

  const [showActions, setShowActions] = useState(false);

  const style = useMemo(() => ({
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }), [transform, transition, isDragging]);

  const isActive = product.active !== false;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative p-3 sm:p-4 hover:bg-gray-50 dark:hover:bg-slate-700/40 border-b last:border-b-0 ${
        isDragging ? 'bg-orange-50 dark:bg-orange-900/20 shadow-lg rounded-lg z-50' : ''
      } ${!isActive ? 'bg-gray-100 dark:bg-slate-800/50 opacity-60' : ''}`}
    >
      {/* Mobile Layout (< 768px) */}
      <div className="flex flex-col gap-3 md:hidden">
        {/* Row 1: Drag Handle, Product Info, 3-Dot Menu */}
        <div className="flex items-start gap-3">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing touch-none p-1 hover:bg-gray-100 dark:bg-gray-800 rounded mt-1"
          >
            <GripVertical className="h-5 w-5 text-gray-400" />
          </button>
          <Package className={`h-5 w-5 mt-1 ${isActive ? 'text-gray-400' : 'text-gray-300'}`} />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className={`font-medium text-base ${!isActive ? 'text-gray-400 line-through' : 'text-slate-900 dark:text-slate-100'}`}>
                {product.name}
              </span>
              {!isActive && (
                <Badge variant="secondary" className="text-xs bg-gray-200 text-gray-500">
                  <EyeOff className="h-3 w-3 mr-1" />
                  Inaktiv
                </Badge>
              )}
            </div>
            {product.article_number && (
              <span className={`text-xs flex items-center gap-1 mt-1 ${isActive ? 'text-gray-500' : 'text-gray-400'}`}>
                <Hash className="h-3 w-3" />
                {product.article_number}
              </span>
            )}
          </div>
          {/* 3-Dot Menu */}
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-2 hover:bg-gray-200 rounded-full"
          >
            <MoreVertical className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Row 2: Active/Inactive Toggle */}
        <div className="flex items-center justify-between pl-12">
          <div className="flex items-center gap-2">
            <Switch
              checked={isActive}
              onCheckedChange={() => onToggleActive(product)}
              className="data-[state=checked]:bg-green-500"
            />
            <span className={`text-sm font-medium ${isActive ? 'text-green-600' : 'text-gray-400'}`}>
              {isActive ? '🟢 Aktiv' : '⚫ Inaktiv'}
            </span>
          </div>
        </div>

        {/* Actions Menu (Collapsible) */}
        {showActions && (
          <div className="pl-12 flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onEdit(product);
                setShowActions(false);
              }}
              className="flex-1 text-blue-600 border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            >
              <Pencil className="h-4 w-4 mr-2" />
              Bearbeiten
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onDelete(product);
                setShowActions(false);
              }}
              className="flex-1 text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Löschen
            </Button>
          </div>
        )}
      </div>

      {/* Desktop Layout (>= 768px) - Original Design */}
      <div className="hidden md:flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing touch-none p-1 hover:bg-gray-100 dark:bg-gray-800 rounded"
          >
            <GripVertical className="h-5 w-5 text-gray-400" />
          </button>
          <Package className={`h-5 w-5 ${isActive ? 'text-gray-400' : 'text-gray-300'}`} />
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className={`font-medium ${!isActive ? 'text-gray-400 line-through' : ''}`}>
                {product.name}
              </span>
              {!isActive && (
                <Badge variant="secondary" className="text-xs bg-gray-200 text-gray-500">
                  <EyeOff className="h-3 w-3 mr-1" />
                  Inaktiv
                </Badge>
              )}
            </div>
            {product.article_number && (
              <span className={`text-xs flex items-center gap-1 ${isActive ? 'text-gray-500' : 'text-gray-400'}`}>
                <Hash className="h-3 w-3" />
                {product.article_number}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Active/Inactive Toggle */}
          <div className="flex items-center gap-2">
            <Switch
              checked={isActive}
              onCheckedChange={() => onToggleActive(product)}
              className="data-[state=checked]:bg-green-500"
            />
            <span className={`text-xs ${isActive ? 'text-green-600' : 'text-gray-400'}`}>
              {isActive ? 'Aktiv' : 'Inaktiv'}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(product)}
          >
            <Pencil className="h-4 w-4 text-gray-500" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(product)}
            className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
});

// Drag Overlay Item (shown while dragging)
const DragOverlayItem = ({ product }) => {
  if (!product) return null;
  
  const isActive = product.active !== false;
  
  return (
    <div className={`flex items-center gap-3 p-4 bg-white dark:bg-slate-800 shadow-xl rounded-lg border-2 border-orange-400 ${!isActive ? 'opacity-60' : ''}`}>
      <GripVertical className="h-5 w-5 text-gray-400" />
      <Package className="h-5 w-5 text-orange-500" />
      <div className="flex flex-col">
        <span className={`font-medium ${!isActive ? 'line-through text-gray-400' : ''}`}>{product.name}</span>
        {product.article_number && (
          <span className="text-xs text-gray-500">#{product.article_number}</span>
        )}
      </div>
    </div>
  );
};

// Droppable Zone for empty categories - Memoized
const CategoryDropZone = memo(({ categoryId, totalCount }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `category-${categoryId}`,
  });

  return (
    <div 
      ref={setNodeRef}
      className={`p-6 text-center text-muted-foreground border-2 border-dashed m-2 rounded-lg transition-colors ${
        isOver 
          ? 'border-orange-400 bg-orange-50' 
          : 'border-gray-200'
      }`}
    >
      <Package className={`h-8 w-8 mx-auto mb-2 ${isOver ? 'text-orange-500' : 'opacity-50'}`} />
      <p className={isOver ? 'text-orange-600 font-medium' : ''}>
        {isOver 
          ? 'Hier ablegen'
          : totalCount === 0 
            ? 'Keine Produkte - hierher ziehen oder neu erstellen'
            : 'Alle Produkte in dieser Kategorie sind inaktiv'
        }
      </p>
    </div>
  );
});

export const BackshopListe = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [activeProduct, setActiveProduct] = useState(null);
  const [showInactive, setShowInactive] = useState(true);
  
  // Category Dialog
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  const [categorySelectedLocations, setCategorySelectedLocations] = useState({});
  const [savingCategory, setSavingCategory] = useState(false);
  
  // Product Dialog
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productName, setProductName] = useState('');
  const [productEan, setProductEan] = useState('');
  const [productArticleNumber, setProductArticleNumber] = useState('');
  const [productCategoryId, setProductCategoryId] = useState('');
  const [productActive, setProductActive] = useState(true);
  const [productLabels, setProductLabels] = useState([]);
  const [productSelectedLocations, setProductSelectedLocations] = useState({});
  const [savingProduct, setSavingProduct] = useState(false);
  const [productMasterProductId, setProductMasterProductId] = useState(null);
  // Handbuch PDF-Felder
  const [productBeschreibung, setProductBeschreibung] = useState('');
  const [productPdfFile, setProductPdfFile] = useState(null);
  const [productCurrentThumbnailUrl, setProductCurrentThumbnailUrl] = useState(null);
  const [productCurrentPdfUrl, setProductCurrentPdfUrl] = useState(null);
  const pdfFileRef = useRef();
  // Vitrinenbild-Felder
  const [productImageFile, setProductImageFile] = useState(null);
  const [productCurrentImageUrl, setProductCurrentImageUrl] = useState(null);
  const imageFileRef = useRef();

  // ── AP3 Duplikat-Check ────────────────────────────────────────────────────
  const [duplikatMatch, setDuplikatMatch]   = useState(null);
  const [duplikatLoading, setDuplikatLoading] = useState(false);

  // ── Smart-Match ───────────────────────────────────────────────────────────
  const [smartMatchResult, setSmartMatchResult] = useState(null);  // { master_match, own_duplicate }
  const [smartMatchLoading, setSmartMatchLoading] = useState(false);
  const [showSmartMatchModal, setShowSmartMatchModal] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null); // own duplicate product name

  // Delete Dialogs
  const [deleteCategoryDialog, setDeleteCategoryDialog] = useState(null);
  const [deleteProductDialog, setDeleteProductDialog] = useState(null);

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchLocations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/locations`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setLocations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      toast.error('Fehler beim Laden der Standorte');
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [catRes, prodRes] = await Promise.all([
        axios.get(`${API}/backshop/categories`),
        axios.get(`${API}/backshop/products`)
      ]);
      
      setCategories(catRes.data || []);
      setProducts(prodRes.data || []);
      
      // Expand all categories by default
      const expanded = {};
      (catRes.data || []).forEach(c => {
        expanded[c.id] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchLocations(); // NEW: Fetch locations on mount
  }, [fetchData, fetchLocations]);

  // Category Functions
  const openAddCategory = useCallback(() => {
    setEditingCategory(null);
    setCategoryName('');
    
    // NEW: Default all locations selected
    const allSelected = {};
    locations.forEach(loc => {
      allSelected[loc.id] = true;
    });
    setCategorySelectedLocations(allSelected);
    
    setCategoryDialogOpen(true);
  }, [locations]);

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryName(category.name);
    
    // NEW: Build selectedLocations from location_ids
    const selectedLocs = {};
    (category.location_ids || []).forEach(locId => {
      selectedLocs[locId] = true;
    });
    setCategorySelectedLocations(selectedLocs);
    
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }

    // NEW: Build location_ids array from selectedLocations
    const location_ids = Object.keys(categorySelectedLocations)
      .filter(locId => categorySelectedLocations[locId]);

    if (location_ids.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Standort aus');
      return;
    }

    setSavingCategory(true);
    try {
      if (editingCategory) {
        await axios.put(`${API}/backshop/categories/${editingCategory.id}`, {
          name: categoryName,
          location_ids: location_ids, // NEW
          order: editingCategory.order
        });
        toast.success('Kategorie aktualisiert');
      } else {
        await axios.post(`${API}/backshop/categories`, {
          name: categoryName,
          location_ids: location_ids, // NEW
          order: categories.length
        });
        toast.success('Kategorie erstellt');
      }
      setCategoryDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    try {
      await axios.delete(`${API}/backshop/categories/${category.id}`);
      toast.success('Kategorie gelöscht');
      setDeleteCategoryDialog(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  // Product Functions
  const openAddProduct = (categoryId) => {
    setEditingProduct(null);
    setProductName('');
    setProductArticleNumber('');
    setProductEan('');
    setProductCategoryId(categoryId);
    setProductActive(true);
    setProductLabels([]);
    setProductBeschreibung('');
    setProductPdfFile(null);
    setProductCurrentThumbnailUrl(null);
    setProductCurrentPdfUrl(null);
    setProductImageFile(null);
    setProductCurrentImageUrl(null);
    setProductMasterProductId(null);
    setSmartMatchResult(null);
    setDuplicateWarning(null);
    
    // NEW: Default all locations selected
    const allSelected = {};
    locations.forEach(loc => {
      allSelected[loc.id] = true;
    });
    setProductSelectedLocations(allSelected);
    
    setProductDialogOpen(true);
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductName(product.name);
    setProductArticleNumber(product.article_number || '');
    setProductEan(product.ean || '');
    setProductCategoryId(product.category_id);
    setProductActive(product.active !== false);
    setProductMasterProductId(product.master_product_id || null);
    setSmartMatchResult(null);
    setDuplicateWarning(null);
    setProductLabels(product.labels || []);
    setProductBeschreibung(product.beschreibung || '');
    setProductPdfFile(null);
    setProductCurrentThumbnailUrl(product.thumbnail_url || null);
    setProductCurrentPdfUrl(product.pdf_url || null);
    setProductImageFile(null);
    setProductCurrentImageUrl(product.image_url || null);
    
    // NEW: Build selectedLocations from location_ids
    const selectedLocs = {};
    (product.location_ids || []).forEach(locId => {
      selectedLocs[locId] = true;
    });
    setProductSelectedLocations(selectedLocs);
    
    setProductDialogOpen(true);
  };

  const handleSmartMatchBlur = async (value, type) => {
    if (!value?.trim() || editingProduct) return; // nur bei neuen Produkten
    setSmartMatchLoading(true);
    setDuplicateWarning(null);
    try {
      const token = localStorage.getItem('token');
      // Pass first selected location_id for brand-aware matching
      const firstLocationId = Object.keys(productSelectedLocations).find(id => productSelectedLocations[id]);
      const locationParam = firstLocationId ? `&location_id=${firstLocationId}` : '';
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/smart-match?query=${encodeURIComponent(value.trim())}&type=${type}${locationParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { master_match, own_duplicate } = res.data;

      if (own_duplicate) {
        setDuplicateWarning(own_duplicate.name);
      }
      if (master_match) {
        setSmartMatchResult({ ...master_match, _matchType: type });
        setShowSmartMatchModal(true);
      }
    } catch (e) {
      // Stiller Fehler – Smart-Match ist optional
    } finally {
      setSmartMatchLoading(false);
    }
  };

  const applyMasterMatch = (match) => {
    const brand = user?.brand || '';
    if (match.product_name) setProductName(match.product_name);
    if (match.brand_numbers?.[brand]) setProductArticleNumber(match.brand_numbers[brand]);
    if (match.ean) setProductEan(match.ean);
    if (match.description) setProductBeschreibung(match.description);
    if (match.brand_media?.[brand]?.image_url) setProductCurrentImageUrl(match.brand_media[brand].image_url);
    if (match.brand_media?.[brand]?.pdf_url) setProductCurrentPdfUrl(match.brand_media[brand].pdf_url);
    setProductMasterProductId(match.id);
    setShowSmartMatchModal(false);
    toast.success('Daten aus dem Master-Katalog übernommen!');
  };

  const handleSaveProduct = async (skipDuplikatCheck = false) => {
    if (!productName.trim()) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }
    if (!productCategoryId) {
      toast.error('Bitte wählen Sie eine Kategorie');
      return;
    }

    // NEW: Build location_ids array from selectedLocations
    const location_ids = Object.keys(productSelectedLocations)
      .filter(locId => productSelectedLocations[locId]);

    if (location_ids.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Standort aus');
      return;
    }

    // ── AP3: Duplikat-Check nur bei neuem Produkt mit Artikelnummer ──────────
    if (!editingProduct && !skipDuplikatCheck && (productArticleNumber?.trim() || productEan?.trim())) {
      setDuplikatLoading(true);
      try {
        const res = await axios.post(`${API}/backshop/check-duplicate`, {
          article_number: productArticleNumber?.trim() || '',
          ean: productEan?.trim() || '',
        }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        const { result, matches } = res.data;
        if (result === 'single_match' && matches?.length === 1) {
          setDuplikatMatch(matches[0]);
          setDuplikatLoading(false);
          return; // Warte auf Dialog-Entscheidung
        }
        // multiple_matches → normal anlegen, Merge-Queue im Backend
      } catch { /* silent */ }
      setDuplikatLoading(false);
    }

    setSavingProduct(true);
    try {
      let savedProduct;
      if (editingProduct) {
        const res = await axios.put(`${API}/backshop/products/${editingProduct.id}`, {
          name: productName,
          ean: productEan || null,
          article_number: productArticleNumber || null,
          category_id: productCategoryId,
          location_ids: location_ids,
          order: editingProduct.order || 0,
          active: productActive,
          labels: productLabels,
          master_product_id: productMasterProductId,
        });
        savedProduct = res.data;
        toast.success('Produkt aktualisiert');
      } else {
        const res = await axios.post(`${API}/backshop/products`, {
          name: productName,
          ean: productEan || null,
          article_number: productArticleNumber || null,
          category_id: productCategoryId,
          location_ids: location_ids,
          active: productActive,
          labels: productLabels,
          master_product_id: productMasterProductId,
        });
        savedProduct = res.data;
        toast.success('Produkt erstellt');
      }

      // LEARNING SYSTEM: Vorschlag senden wenn EAN-Match + keine brand_number + manuell eingegeben
      if (
        !editingProduct &&
        productMasterProductId &&
        productArticleNumber?.trim() &&
        smartMatchResult?._matchType === 'ean'
      ) {
        const firstLocationId = Object.keys(productSelectedLocations).find(id => productSelectedLocations[id]);
        const existingBrandNumber = smartMatchResult?.brand_numbers?.[user?.brand];
        if (!existingBrandNumber) {
          try {
            const token = localStorage.getItem('token');
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/brand-number-suggestion`, {
              master_product_id: productMasterProductId,
              article_number: productArticleNumber.trim(),
              location_id: firstLocationId || null
            }, { headers: { Authorization: `Bearer ${token}` } });
          } catch {
            // Stiller Fehler – Suggestion ist optional
          }
        }
      }

      // PDF hochladen (falls ausgewählt)
      if (productPdfFile && savedProduct?.id) {
        const formData = new FormData();
        formData.append('beschreibung', productBeschreibung.trim());
        formData.append('pdf_file', productPdfFile);
        await axios.post(`${API}/backshop/products/${savedProduct.id}/pdf`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('PDF hochgeladen');
      } else if (editingProduct && productBeschreibung.trim() !== (editingProduct.beschreibung || '')) {
        // Nur Beschreibung aktualisieren
        const fd = new FormData();
        fd.append('beschreibung', productBeschreibung.trim());
        await axios.patch(`${API}/backshop/products/${editingProduct.id}/beschreibung`, fd);
      }

      // Bild hochladen (falls ausgewählt)
      if (productImageFile && savedProduct?.id) {
        const formData = new FormData();
        formData.append('image_file', productImageFile);
        await axios.post(`${API}/backshop/products/${savedProduct.id}/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Bild hochgeladen');
      }

      setProductDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingProduct(false);
    }
  };

  const handleDeleteProduct = async (product) => {
    try {
      await axios.delete(`${API}/backshop/products/${product.id}`);
      toast.success('Produkt gelöscht');
      setDeleteProductDialog(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleToggleActive = useCallback(async (product) => {
    const newActiveState = product.active === false ? true : false;
    
    try {
      await axios.put(`${API}/backshop/products/${product.id}`, {
        name: product.name,
        article_number: product.article_number || null,
        category_id: product.category_id,
        order: product.order || 0,
        active: newActiveState
      });
      
      // Update local state immediately
      setProducts(prev => prev.map(p => 
        p.id === product.id ? { ...p, active: newActiveState } : p
      ));
      
      toast.success(newActiveState ? 'Produkt aktiviert' : 'Produkt deaktiviert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  }, []);

  const toggleCategory = useCallback((categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  }, []);

  // Memoized product calculations
  const productsByCategory = useMemo(() => {
    const result = {};
    categories.forEach(cat => {
      result[cat.id] = products
        .filter(p => p.category_id === cat.id)
        .filter(p => showInactive || p.active !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    });
    return result;
  }, [products, categories, showInactive]);

  const productCounts = useMemo(() => {
    const result = {};
    categories.forEach(cat => {
      result[cat.id] = {
        active: products.filter(p => p.category_id === cat.id && p.active !== false).length,
        total: products.filter(p => p.category_id === cat.id).length
      };
    });
    return result;
  }, [products, categories]);

  const { totalActiveProducts, totalInactiveProducts } = useMemo(() => ({
    totalActiveProducts: products.filter(p => p.active !== false).length,
    totalInactiveProducts: products.filter(p => p.active === false).length
  }), [products]);

  // Drag & Drop Handlers
  const handleDragStart = useCallback((event) => {
    const { active } = event;
    const draggedProduct = products.find(p => p.id === active.id);
    setActiveProduct(draggedProduct);
  }, [products]);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    setActiveProduct(null);

    if (!over) return;

    const activeProductItem = products.find(p => p.id === active.id);
    if (!activeProductItem) return;

    // Find which category the item was dropped into
    let targetCategoryId = activeProductItem.category_id;
    let overProduct = products.find(p => p.id === over.id);
    
    if (overProduct) {
      targetCategoryId = overProduct.category_id;
    } else if (over.id.startsWith('category-')) {
      targetCategoryId = over.id.replace('category-', '');
    }

    // Get products in the target category
    const categoryProducts = products
      .filter(p => p.category_id === targetCategoryId)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    // Find the indices
    const oldIndex = categoryProducts.findIndex(p => p.id === active.id);
    const newIndex = overProduct 
      ? categoryProducts.findIndex(p => p.id === over.id)
      : categoryProducts.length;

    // If moving within the same category
    if (activeProductItem.category_id === targetCategoryId && oldIndex !== -1) {
      if (oldIndex !== newIndex) {
        const newOrder = arrayMove(categoryProducts, oldIndex, newIndex);
        
        // Update local state immediately for better UX
        const updatedProducts = products.map(p => {
          const idx = newOrder.findIndex(np => np.id === p.id);
          if (idx !== -1) {
            return { ...p, order: idx };
          }
          return p;
        });
        setProducts(updatedProducts);

        // Send batch update to server
        try {
          await axios.put(`${API}/backshop/products/batch/reorder`, {
            products: newOrder.map((p, idx) => ({
              id: p.id,
              category_id: targetCategoryId,
              order: idx
            }))
          });
          toast.success('Reihenfolge aktualisiert');
        } catch (error) {
          toast.error('Fehler beim Aktualisieren');
          fetchData(); // Revert on error
        }
      }
    } else {
      // Moving to a different category
      const newCategoryProducts = [
        ...categoryProducts.filter(p => p.id !== active.id),
      ];
      
      // Insert at the position
      const insertIndex = overProduct 
        ? newCategoryProducts.findIndex(p => p.id === over.id)
        : newCategoryProducts.length;
      
      newCategoryProducts.splice(insertIndex, 0, { ...activeProductItem, category_id: targetCategoryId });

      // Update local state
      const updatedProducts = products.map(p => {
        if (p.id === active.id) {
          return { ...p, category_id: targetCategoryId, order: insertIndex };
        }
        const idx = newCategoryProducts.findIndex(np => np.id === p.id);
        if (idx !== -1 && p.category_id === targetCategoryId) {
          return { ...p, order: idx };
        }
        return p;
      });
      setProducts(updatedProducts);

      // Send batch update
      try {
        await axios.put(`${API}/backshop/products/batch/reorder`, {
          products: newCategoryProducts.map((p, idx) => ({
            id: p.id,
            category_id: targetCategoryId,
            order: idx
          }))
        });
        toast.success('Produkt verschoben');
        fetchData(); // Refresh to get correct state
      } catch (error) {
        toast.error('Fehler beim Verschieben');
        fetchData();
      }
    }
  }, [products, fetchData]);

  const handleDragOver = useCallback((event) => {
    const { active, over } = event;
    
    if (!over) return;

    const activeProductItem = products.find(p => p.id === active.id);
    if (!activeProductItem) return;

    // Check if dragging over a category drop zone
    if (over.id.startsWith('category-')) {
      const targetCategoryId = over.id.replace('category-', '');
      if (activeProductItem.category_id !== targetCategoryId) {
        // Visual feedback could be added here
      }
    }
  }, [products]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
              Backshop Liste
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-2">
              Kategorien und Produkte verwalten • Produkte per Drag & Drop verschieben
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <Button 
              onClick={() => navigate('/admin/abschriften/alle-produkte')} 
              variant="outline"
              className="border-orange-600 text-orange-600 hover:bg-orange-50"
            >
              <List className="h-4 w-4 mr-2" />
              Alle Produkte
            </Button>
            <Button onClick={openAddCategory} className="bg-orange-600 hover:bg-orange-700">
              <FolderPlus className="h-4 w-4 mr-2" />
              Kategorie hinzufügen
            </Button>
          </div>
        </div>

        {/* Stats & Filter Bar */}
        <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200 dark:from-orange-950/30 dark:to-amber-950/20 dark:border-orange-900/50">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center justify-between gap-3 sm:gap-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6">
                <div className="flex items-center gap-2">
                  <Eye className="h-5 w-5 text-green-600 dark:text-green-500" />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{totalActiveProducts} aktive Produkte</span>
                </div>
                <div className="flex items-center gap-2">
                  <EyeOff className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <span className="text-sm text-gray-500 dark:text-gray-400">{totalInactiveProducts} inaktive Produkte</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={showInactive}
                  onCheckedChange={setShowInactive}
                  id="show-inactive"
                />
                <Label htmlFor="show-inactive" className="text-sm cursor-pointer text-gray-700 dark:text-gray-300">
                  Inaktive Produkte anzeigen
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {categories.length === 0 ? (
          <Card className="border-2 border-dashed border-orange-300">
            <CardContent className="py-16 text-center">
              <ShoppingBag className="h-12 w-12 text-orange-400 mx-auto mb-4" />
              <p className="text-lg font-medium">Noch keine Kategorien</p>
              <p className="text-muted-foreground mt-2 mb-4">
                Erstellen Sie Ihre erste Kategorie für Backshop-Produkte.
              </p>
              <Button onClick={openAddCategory} className="bg-orange-600 hover:bg-orange-700">
                <FolderPlus className="h-4 w-4 mr-2" />
                Erste Kategorie erstellen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
          >
            <div className="space-y-4">
              {categories.map(category => {
                const categoryProducts = productsByCategory[category.id] || [];
                const counts = productCounts[category.id] || { active: 0, total: 0 };
                const isExpanded = expandedCategories[category.id];
                
                return (
                  <Card key={category.id} className="overflow-hidden">
                    <CardHeader 
                      className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 cursor-pointer"
                      onClick={() => toggleCategory(category.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDown className="h-5 w-5" />
                          ) : (
                            <ChevronRight className="h-5 w-5" />
                          )}
                          <ShoppingBag className="h-5 w-5" />
                          <CardTitle className="text-lg">{category.name}</CardTitle>
                          <Badge variant="secondary" className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                            {counts.active}/{counts.total} aktiv
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditCategory(category)}
                            className="text-white hover:bg-white dark:bg-gray-900/20"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteCategoryDialog(category)}
                            className="text-white hover:bg-red-500/50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    
                    {isExpanded && (
                      <CardContent className="p-0">
                        <SortableContext
                          items={categoryProducts.map(p => p.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          {categoryProducts.length === 0 ? (
                            <CategoryDropZone categoryId={category.id} totalCount={counts.total} />
                          ) : (
                            <div className="divide-y">
                              {categoryProducts.map(product => (
                                <SortableProductItem
                                  key={product.id}
                                  product={product}
                                  onEdit={openEditProduct}
                                  onDelete={setDeleteProductDialog}
                                  onToggleActive={handleToggleActive}
                                />
                              ))}
                            </div>
                          )}
                        </SortableContext>
                        
                        <div className="p-3 bg-gray-50 dark:bg-gray-800 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openAddProduct(category.id)}
                            className="w-full"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Produkt hinzufügen
                          </Button>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
            
            <DragOverlay>
              <DragOverlayItem product={activeProduct} />
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Kategorie bearbeiten' : 'Neue Kategorie'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="categoryName">Name *</Label>
              <Input
                id="categoryName"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="z.B. Trockene, Snacks, Süße Snacks"
              />
            </div>

            {/* NEW: Locations Multi-Select */}
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center gap-2">
                <Label>📍 Verfügbar für Standorte</Label>
                <span className="text-xs text-gray-500">
                  ({Object.values(categorySelectedLocations).filter(Boolean).length} ausgewählt)
                </span>
              </div>
              
              {locations.length === 0 ? (
                <p className="text-sm text-gray-500">Keine Standorte gefunden</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  {locations.map((location) => (
                    <label 
                      key={location.id}
                      className="flex items-start gap-3 p-2 rounded hover:bg-white dark:bg-gray-900 transition-colors cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={categorySelectedLocations[location.id] || false}
                        onChange={(e) => {
                          setCategorySelectedLocations({
                            ...categorySelectedLocations,
                            [location.id]: e.target.checked
                          });
                        }}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-orange-600 focus:ring-orange-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{location.name}</div>
                        {location.address && (
                          <div className="text-xs text-gray-500 truncate">{location.address}</div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <span>💡</span>
                <span>Diese Kategorie ist für alle gewählten Standorte verfügbar</span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleSaveCategory} 
              disabled={savingCategory}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {savingCategory ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Speichern'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? 'Produkt bearbeiten' : 'Neues Produkt'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="productName">Produktname *</Label>
              <Input
                id="productName"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                placeholder="z.B. Brezel, Croissant"
              />
            </div>

            {/* EAN + Artikelnummer mit Smart-Match */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="productEan" className="flex items-center gap-1.5">
                  EAN
                  {smartMatchLoading && <Loader2 className="h-3 w-3 animate-spin text-primary" />}
                </Label>
                <Input
                  id="productEan"
                  value={productEan}
                  onChange={(e) => setProductEan(e.target.value)}
                  onBlur={(e) => handleSmartMatchBlur(e.target.value, 'ean')}
                  placeholder="1234567890123"
                  data-testid="product-ean-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="productArticleNumber" className="flex items-center gap-1.5">
                  Artikelnummer
                  {smartMatchLoading && <Loader2 className="h-3 w-3 animate-spin text-primary" />}
                </Label>
                <Input
                  id="productArticleNumber"
                  value={productArticleNumber}
                  onChange={(e) => setProductArticleNumber(e.target.value)}
                  onBlur={(e) => handleSmartMatchBlur(e.target.value, 'article_number')}
                  placeholder="z.B. JET-001"
                  data-testid="product-article-input"
                />
              </div>
            </div>

            {/* Duplikat-Warnung */}
            {duplicateWarning && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800" data-testid="duplicate-warning">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  <span className="font-medium">Bereits im Sortiment:</span> "{duplicateWarning}"
                </p>
              </div>
            )}

            {/* Master-Match-Banner (wenn übernommen) */}
            {productMasterProductId && !showSmartMatchModal && (
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800" data-testid="master-match-applied">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                <p className="text-xs text-green-700 dark:text-green-400 font-medium">
                  Daten aus dem Master-Katalog übernommen
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="productCategory">Kategorie *</Label>
              <Select value={productCategoryId} onValueChange={setProductCategoryId}>
                <SelectTrigger>
                  <SelectValue placeholder="Kategorie wählen" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* NEW: Locations Multi-Select */}
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center gap-2">
                <Label>📍 Verfügbar für Standorte</Label>
                <span className="text-xs text-gray-500">
                  ({Object.values(productSelectedLocations).filter(Boolean).length} ausgewählt)
                </span>
              </div>
              
              {locations.length === 0 ? (
                <p className="text-sm text-gray-500">Keine Standorte gefunden</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  {locations.map((location) => (
                    <label 
                      key={location.id}
                      className="flex items-start gap-3 p-2 rounded hover:bg-white dark:bg-gray-900 transition-colors cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={productSelectedLocations[location.id] || false}
                        onChange={(e) => {
                          setProductSelectedLocations({
                            ...productSelectedLocations,
                            [location.id]: e.target.checked
                          });
                        }}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-orange-600 focus:ring-orange-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{location.name}</div>
                        {location.address && (
                          <div className="text-xs text-gray-500 truncate">{location.address}</div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <span>💡</span>
                <span>Dieses Produkt ist für alle gewählten Standorte verfügbar</span>
              </p>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex flex-col">
                <Label htmlFor="productActive" className="font-medium">Produkt aktiv</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Inaktive Produkte werden Mitarbeitern nicht angezeigt
                </p>
              </div>
              <Switch
                id="productActive"
                checked={productActive}
                onCheckedChange={setProductActive}
                className="data-[state=checked]:bg-green-500"
              />
            </div>

            {/* ── Sortiments-Labels ── */}
            <div className="border-t pt-3 space-y-2">
              <Label className="text-sm font-medium">
                Sortiments-Zuordnung
                <span className="text-muted-foreground font-normal ml-1">(mehrere möglich)</span>
              </Label>
              <div className="flex flex-wrap gap-2">
                {PRODUCT_LABELS.map(lbl => {
                  const active = productLabels.includes(lbl.key);
                  return (
                    <button
                      key={lbl.key}
                      type="button"
                      onClick={() => setProductLabels(prev =>
                        active ? prev.filter(k => k !== lbl.key) : [...prev, lbl.key]
                      )}
                      className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border-2 transition-all"
                      style={{
                        borderColor: lbl.color,
                        backgroundColor: active ? lbl.color : lbl.bg,
                        color: active ? '#fff' : lbl.color,
                      }}
                      data-testid={`label-toggle-${lbl.key}`}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: active ? '#fff' : lbl.color }} />
                      {lbl.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Handbuch-Bereich ── */}
            <div className="border-t pt-3 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
                <BookOpen className="h-4 w-4" />
                Handbuch (optional)
              </div>
              <div>
                <Label htmlFor="beschreibung-liste">Interne Beschreibung</Label>
                <Input
                  id="beschreibung-liste"
                  value={productBeschreibung}
                  onChange={(e) => setProductBeschreibung(e.target.value)}
                  placeholder="Optionale Notiz für das Handbuch..."
                />
              </div>
              <div>
                <Label>PDF-Rezept</Label>
                {productCurrentPdfUrl && !productPdfFile && (
                  <div className="flex items-center gap-2 mb-2 text-sm text-green-700 dark:text-green-400">
                    <FileText className="h-4 w-4" />
                    <span>PDF vorhanden</span>
                    {productCurrentThumbnailUrl && (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${productCurrentThumbnailUrl}?token=${localStorage.getItem('token')}`}
                        alt="Vorschau"
                        className="h-10 w-10 object-cover rounded border"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await axios.delete(`${API}/backshop/products/${editingProduct.id}/pdf`);
                          setProductCurrentPdfUrl(null);
                          setProductCurrentThumbnailUrl(null);
                          toast.success('PDF entfernt');
                          fetchData();
                        } catch (e) {
                          toast.error('Fehler beim Entfernen');
                        }
                      }}
                      className="text-red-500 hover:text-red-700 ml-1"
                    >
                      <XIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <div
                  className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-center cursor-pointer hover:border-amber-400 transition-colors"
                  onClick={() => pdfFileRef.current?.click()}
                >
                  {productPdfFile ? (
                    <div className="flex items-center justify-center gap-2 text-sm">
                      <FileText className="h-4 w-4 text-amber-500" />
                      <span className="truncate max-w-[180px]">{productPdfFile.name}</span>
                      <button type="button" onClick={(e) => { e.stopPropagation(); setProductPdfFile(null); }}>
                        <XIcon className="h-3 w-3 text-gray-400 hover:text-red-500" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                      <Upload className="h-4 w-4" />
                      <span>{productCurrentPdfUrl ? 'PDF ersetzen' : 'PDF hochladen'}</span>
                    </div>
                  )}
                </div>
                <input
                  ref={pdfFileRef}
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={(e) => setProductPdfFile(e.target.files?.[0] || null)}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Optional · Produkte mit PDF erscheinen im Handbuch
                </p>
              </div>
            </div>

            {/* ── Vitrinenbild-Bereich ── */}
            <div className="border-t pt-3 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-400">
                <ImageIcon className="h-4 w-4" />
                Vitrinenbild (optional)
              </div>
              {productCurrentImageUrl && !productImageFile && (
                <div className="flex items-center gap-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/backshop/products/${editingProduct?.id}/image?token=${localStorage.getItem('token')}`}
                    alt="Vitrinenbild"
                    className="h-16 w-16 object-cover rounded border border-blue-200"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="flex-1">
                    <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">Bild vorhanden</p>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await axios.delete(`${API}/backshop/products/${editingProduct.id}/image`);
                          setProductCurrentImageUrl(null);
                          toast.success('Bild entfernt');
                          fetchData();
                        } catch (e) {
                          toast.error('Fehler beim Entfernen');
                        }
                      }}
                      className="text-xs text-red-500 hover:text-red-700 mt-1 flex items-center gap-1"
                    >
                      <XIcon className="h-3 w-3" /> Bild löschen
                    </button>
                  </div>
                </div>
              )}
              <div
                className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-center cursor-pointer hover:border-blue-400 transition-colors"
                onClick={() => imageFileRef.current?.click()}
              >
                {productImageFile ? (
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <ImageIcon className="h-4 w-4 text-blue-500" />
                    <span className="truncate max-w-[180px]">{productImageFile.name}</span>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setProductImageFile(null); }}>
                      <XIcon className="h-3 w-3 text-gray-400 hover:text-red-500" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                    <Upload className="h-4 w-4" />
                    <span>{productCurrentImageUrl ? 'Bild ersetzen' : 'Bild hochladen'}</span>
                  </div>
                )}
              </div>
              <input
                ref={imageFileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                className="hidden"
                onChange={(e) => setProductImageFile(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-gray-400">
                Optional · JPG/PNG/WEBP · max. 10 MB · Wird im Vitrinen-Baukasten angezeigt
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleSaveProduct} 
              disabled={savingProduct}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {savingProduct ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Speichern'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Category Dialog */}
      <AlertDialog open={!!deleteCategoryDialog} onOpenChange={() => setDeleteCategoryDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kategorie löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Die Kategorie &ldquo;{deleteCategoryDialog?.name}&rdquo; und alle zugehörigen Produkte werden gelöscht. 
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteCategory(deleteCategoryDialog)}
              className="bg-red-600 hover:bg-red-700"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Product Dialog */}
      <AlertDialog open={!!deleteProductDialog} onOpenChange={() => setDeleteProductDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Produkt löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Das Produkt &ldquo;{deleteProductDialog?.name}&rdquo; wird gelöscht. 
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteProduct(deleteProductDialog)}
              className="bg-red-600 hover:bg-red-700"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── SMART-MATCH MODAL ──────────────────────────────────────────────── */}
      <Dialog open={showSmartMatchModal} onOpenChange={(o) => { if (!o) setShowSmartMatchModal(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-5 w-5 text-amber-500" />
              Produkt in der Zentraldatenbank gefunden!
            </DialogTitle>
          </DialogHeader>

          {smartMatchResult && (
            <div className="py-2 space-y-4">
              {/* Produkt-Vorschau */}
              <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border">
                {/* Bild */}
                <div className="w-20 h-20 rounded-lg overflow-hidden border bg-white flex-shrink-0 flex items-center justify-center">
                  {smartMatchResult.brand_media?.[user?.brand]?.image_url ? (
                    <img
                      src={smartMatchResult.brand_media[user?.brand].image_url}
                      alt={smartMatchResult.product_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Package className="h-8 w-8 text-gray-300" />
                  )}
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0 space-y-1.5">
                  <p className="font-semibold text-sm leading-tight">{smartMatchResult.product_name}</p>
                  {smartMatchResult.brand_numbers?.[user?.brand] && (
                    <p className="text-xs text-muted-foreground">
                      Art.Nr.: <span className="font-mono">{smartMatchResult.brand_numbers[user?.brand]}</span>
                    </p>
                  )}
                  {smartMatchResult.ean && (
                    <p className="text-xs text-muted-foreground">
                      EAN: <span className="font-mono">{smartMatchResult.ean}</span>
                    </p>
                  )}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {smartMatchResult.brand_media?.[user?.brand]?.image_url && (
                      <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400 px-1.5 py-0.5 rounded">
                        Bild vorhanden
                      </span>
                    )}
                    {smartMatchResult.brand_media?.[user?.brand]?.pdf_url && (
                      <span className="text-xs bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400 px-1.5 py-0.5 rounded">
                        PDF vorhanden
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-sm text-muted-foreground">
                Möchten Sie die offiziellen Daten aus dem Master-Katalog in Ihr Formular übernehmen?
                Bild, PDF und Beschreibung werden automatisch eingefügt.
              </p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSmartMatchModal(false)}
            >
              Ignorieren
            </Button>
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => smartMatchResult && applyMasterMatch(smartMatchResult)}
              data-testid="apply-master-match-btn"
            >
              <CheckCircle className="h-4 w-4 mr-1.5" />
              Daten &amp; Medien übernehmen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── AP3: Duplikat-Dialog ────────────────────────────────────────── */}
      <BackshopDuplikatDialog
        match={duplikatMatch}
        onAccept={async () => {
          if (!duplikatMatch) return;
          setSavingProduct(true);
          try {
            await axios.post(
              `${API}/admin/master-products/${duplikatMatch.id}/accept`,
              { local_price: null },
              { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            toast.success(`"${duplikatMatch.product_name}" in dein Sortiment übernommen`);
            setDuplikatMatch(null);
            setProductDialogOpen(false);
            fetchData();
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler beim Übernehmen');
          } finally { setSavingProduct(false); }
        }}
        onEnrichImage={async (file) => {
          if (!duplikatMatch) return;
          try {
            await axios.post(`${API}/backshop/master/${duplikatMatch.id}/enrich`, file,
              { headers: { 'Content-Type': file.type, Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            toast.success('Bild hochgeladen und im Katalog hinterlegt');
            setDuplikatMatch(m => m ? { ...m, has_image: true } : m);
          } catch { toast.error('Fehler beim Bild-Upload'); }
        }}
        onEnrichPdf={async (file) => {
          if (!duplikatMatch) return;
          try {
            await axios.post(`${API}/backshop/master/${duplikatMatch.id}/enrich`, file,
              { headers: { 'Content-Type': 'application/pdf', Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            toast.success('PDF hochgeladen und im Katalog hinterlegt');
            setDuplikatMatch(m => m ? { ...m, has_pdf: true } : m);
          } catch { toast.error('Fehler beim PDF-Upload'); }
        }}
        onCancel={() => {
          setDuplikatMatch(null);
          handleSaveProduct(true); // skipDuplikatCheck=true → lokal anlegen
        }}
        loading={savingProduct}
      />
    </DashboardLayout>
  );
};
